import { DsmGrid } from "@dsm-dcs/design-system-react";
import { Dispatch, FC, SetStateAction } from "react";
import ReactHookDsmSelect from "../../../../../modules/Helpers/ReactHookDsmSelect2";
import EditModeButtons from "../../../helpers/EditModeButtons";
import { FormType } from "../../common";
import { unitLong } from "../../../../utils/unit-utils";
import { GrowingPurpose } from "../../../../../../graphql/generated/blonk/cattle";
import ReactHookDsmInput from "../../../../../modules/Helpers/ReactHookDsmInput2";
import { BeefInputGrowing } from "../../../../models/Baseline/BeefBaseline";
import { useIntl } from "../../../../../../_metronic/i18n/customUseIntl";
import { CSSClassesList } from "../../../../helpers/helperTypes";
import { processAndStageStylesV2 } from "../../../../../../_metronic/layout";
import { UserProfilePrefs } from "../../../../../modules/Helpers/UserProfilePrefs";
import { useFormContext } from "react-hook-form";
import RowTextFieldWithMetrics from "../../../../../modules/Farms/Intervention/RowControlledTextFieldWithMetrics2";
import { Spacer } from "../../../helpers/Spacer";
import { BeefIntervention } from "../../../../models/Intervention/BeefIntervention";
import { isFieldFilled } from "../CommonDataParts/InterventionGenericInfoDialog";

interface BeefInputGrowingNewAnimalsComponentProps {
  formType: FormType;
  fieldItemPrefix: string;
  defaultStageData: BeefInputGrowing;
  isNewAnimalsEdit: boolean;
  setIsNewAnimalsEdit: Dispatch<SetStateAction<boolean>>;
  newAnimalsCancelHandler: (isInnerCancel?: boolean) => void;
}

const BeefInputGrowingNewAnimalsComponent: FC<BeefInputGrowingNewAnimalsComponentProps> = ({
  formType,
  fieldItemPrefix,
  defaultStageData,
  isNewAnimalsEdit,
  setIsNewAnimalsEdit,
  newAnimalsCancelHandler,
}) => {
  const intl = useIntl();
  const classes: CSSClassesList = processAndStageStylesV2();
  const userUOM = UserProfilePrefs.getInstance().getUserUnitPrefs();
  const fc = useFormContext<BeefIntervention>();

  const growingPurposeOptions = () =>
    Object.entries(GrowingPurpose).map(([key, value]) => ({
      text: key.replace(/([a-z])([A-Z])/g, "$1 $2"),
      value,
    }));

  return (
    <DsmGrid className={classes.dsmGridTwoColumnNoRowSpacingWithBorder}>
      {!isNewAnimalsEdit ? (
        <>
          {isFieldFilled(fc.getValues(`${fieldItemPrefix}.averageAgeAtStart`)) ? (
            <div>
              <RowTextFieldWithMetrics
                name={`${fieldItemPrefix}.averageAgeAtStart`}
                label={intl.formatMessage({ id: "SUSTELL.STAGE.BEEF.INPUT.AVERAGE_AGE_NEW_ANIMALS" })}
                tooltip={intl.formatMessage({ id: "SUSTELL.STAGE.BEEF.INPUT.AVERAGE_AGE_NEW_ANIMALS.TOOLTIP" })}
                baseline={defaultStageData.averageAgeAtStart}
                displayTypeSelect={false}
                type="number"
                metricUnit={intl.formatMessage({ id: "SUSTELL.STAGE.BEEF.INPUT.AVERAGE_AGE_NEW_ANIMALS.ADORNMENT" })}
                disabled
              />
              <Spacer />
            </div>
          ) : (
            <ReactHookDsmInput
              name={`${fieldItemPrefix}.originalAverageAgeAtStart`}
              label={intl.formatMessage({ id: "SUSTELL.STAGE.BEEF.INPUT.AVERAGE_AGE_NEW_ANIMALS" })}
              tooltip={intl.formatMessage({ id: "SUSTELL.STAGE.BEEF.INPUT.AVERAGE_AGE_NEW_ANIMALS.TOOLTIP" })}
              adornment={intl.formatMessage({ id: "SUSTELL.STAGE.BEEF.INPUT.AVERAGE_AGE_NEW_ANIMALS.ADORNMENT" })}
              defaultValue={defaultStageData.averageAgeAtStart}
              type="number"
              disabled
            />
          )}
          <ReactHookDsmSelect
            name={`${fieldItemPrefix}.growingPurpose`}
            label={intl.formatMessage({ id: "SUSTELL.STAGE.BEEF.INPUT.GROWING_PURPOSE" })}
            tooltip={intl.formatMessage({ id: "SUSTELL.STAGE.BEEF.INPUT.GROWING_PURPOSE.TOOLTIP" })}
            defaultValue={defaultStageData.growingPurpose.toString()}
            options={growingPurposeOptions()}
            disabled
          />
          {isFieldFilled(fc.getValues(`${fieldItemPrefix}.averageWeightNewAnimals`)) ? (
            <div>
              <RowTextFieldWithMetrics
                name={`${fieldItemPrefix}.averageWeightNewAnimals`}
                label={intl.formatMessage({ id: "SUSTELL.STAGE.BEEF.INPUT.AVERAGE_WEIGHT_NEW_ANIMALS" })}
                tooltip={intl.formatMessage({ id: "SUSTELL.STAGE.BEEF.INPUT.AVERAGE_WEIGHT_NEW_ANIMALS.TOOLTIP" })}
                baseline={defaultStageData.averageWeightNewAnimals}
                displayTypeSelect={false}
                type="number"
                metricUnit={intl.formatMessage(
                  { id: "SUSTELL.STAGE.BEEF.INPUT.AVERAGE_WEIGHT_NEW_ANIMALS.ADORNMENT" },
                  { unit: userUOM?.unitOutputMass ? unitLong(userUOM.unitOutputMass) : "kg" }
                )}
                disabled
              />
              <Spacer />
            </div>
          ) : (
            <ReactHookDsmInput
              name={`${fieldItemPrefix}.originalAverageWeightNewAnimals`}
              label={intl.formatMessage({ id: "SUSTELL.STAGE.BEEF.INPUT.AVERAGE_WEIGHT_NEW_ANIMALS" })}
              tooltip={intl.formatMessage({ id: "SUSTELL.STAGE.BEEF.INPUT.AVERAGE_WEIGHT_NEW_ANIMALS.TOOLTIP" })}
              adornment={intl.formatMessage(
                { id: "SUSTELL.STAGE.BEEF.INPUT.AVERAGE_WEIGHT_NEW_ANIMALS.ADORNMENT" },
                { unit: userUOM?.unitOutputMass ? unitLong(userUOM.unitOutputMass) : "kg" }
              )}
              defaultValue={defaultStageData.averageWeightNewAnimals}
              type="number"
              disabled
            />
          )}
        </>
      ) : (
        <>
          <div>
            <RowTextFieldWithMetrics
              name={`${fieldItemPrefix}.averageAgeAtStart`}
              label={intl.formatMessage({ id: "SUSTELL.STAGE.BEEF.INPUT.AVERAGE_AGE_NEW_ANIMALS" })}
              tooltip={intl.formatMessage({ id: "SUSTELL.STAGE.BEEF.INPUT.AVERAGE_AGE_NEW_ANIMALS.TOOLTIP" })}
              baseline={defaultStageData.averageAgeAtStart}
              type="number"
              metricUnit={intl.formatMessage({ id: "SUSTELL.STAGE.BEEF.INPUT.AVERAGE_AGE_NEW_ANIMALS.ADORNMENT" })}
              disabled={false}
            />
            <Spacer />
          </div>
          <ReactHookDsmSelect
            name={`${fieldItemPrefix}.originalGrowingPurpose`}
            label={intl.formatMessage({ id: "SUSTELL.STAGE.BEEF.INPUT.GROWING_PURPOSE" })}
            tooltip={intl.formatMessage({ id: "SUSTELL.STAGE.BEEF.INPUT.GROWING_PURPOSE.TOOLTIP" })}
            defaultValue={defaultStageData.growingPurpose.toString()}
            options={growingPurposeOptions()}
            disabled
          />
          <div>
            <RowTextFieldWithMetrics
              name={`${fieldItemPrefix}.averageWeightNewAnimals`}
              label={intl.formatMessage({ id: "SUSTELL.STAGE.BEEF.INPUT.AVERAGE_WEIGHT_NEW_ANIMALS" })}
              tooltip={intl.formatMessage({ id: "SUSTELL.STAGE.BEEF.INPUT.AVERAGE_WEIGHT_NEW_ANIMALS.TOOLTIP" })}
              baseline={defaultStageData.averageWeightNewAnimals}
              type="number"
              metricUnit={intl.formatMessage(
                { id: "SUSTELL.STAGE.BEEF.INPUT.AVERAGE_WEIGHT_NEW_ANIMALS.ADORNMENT" },
                { unit: userUOM?.unitOutputMass ? unitLong(userUOM.unitOutputMass) : "kg" }
              )}
              disabled={false}
            />
            <Spacer />
          </div>
        </>
      )}
      <div />
      {formType !== FormType.View &&
        <EditModeButtons
          isEditing={isNewAnimalsEdit}
          onEdit={() => setIsNewAnimalsEdit(true)}
          onCancel={newAnimalsCancelHandler}
        />}
    </DsmGrid>
  )
}

export default BeefInputGrowingNewAnimalsComponent;
